import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "../css/howitworks.css";
import Navbar from "../components/navbar";

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Resumify | How We Work</title>
        <meta
          name="description"
          content="Discover how Resumify turns your work history into interview-winning offers. Learn our simple 4-step process for AI rewriting, ATS optimisation, and PDF export."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://resumify.co.uk/how-it-works" />

        {/* Open Graph Tags for rich link sharing */}
        <meta
          property="og:title"
          content="Resumify | How Our AI CV Builder Works"
        />
        <meta
          property="og:description"
          content="Discover how Resumify turns your work history into interview-winning offers. Learn our simple 4-step process for AI rewriting, ATS optimisation, and PDF export."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://resumify.co.uk/how-it-works" />
      </Helmet>

      <div className="how-it-works">
        <Navbar />

        <header className="hiw-hero">
          <h1>
            How We Turn <span className="text-gradient">Data Into Offers</span>
          </h1>
          <p className="hiw-hero-sub">
            You don't need to be a professional copywriter or an ATS expert.
            Upload your work history, and let our AI do the heavy lifting in
            four simple steps.
          </p>
        </header>

        <section className="steps-section">
          <div className="steps-container">
            {/* STEP 1: Import */}
            <div className="step-row">
              <div className="step-content">
                <div className="step-number">01</div>
                <h2>Import Your Past</h2>
                <p>
                  Start by uploading your dusty old CV document. Our parser
                  instantly extracts your work history, education, and skills.
                  No tedious manual data entry required to get started.
                </p>
              </div>
              <div className="step-visual">
                <div className="abstract-ui-box">
                  <div className="ui-header">📄 Upload Document</div>
                  <div className="ui-body upload-zone">
                    <span className="upload-icon">⬆️</span>
                    <p>Select CV.pdf / docx</p>
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 2: Target (REVERSE LAYOUT) */}
            <div className="step-row reverse">
              <div className="step-content">
                <div className="step-number">02</div>
                <h2>Target Your Future</h2>
                <p>
                  Tell us your target Job Title. Our engine uses this precise
                  context to structure your document, suggest relevant skills,
                  and score your CV against what an employer's Applicant
                  Tracking System (ATS) is programmed to look for.
                </p>
              </div>
              <div className="step-visual">
                <div className="abstract-ui-box">
                  <div className="ui-header">⚙️ Document Settings</div>
                  <div className="ui-body">
                    <label className="mock-label">Target Job Title</label>
                    <div className="mock-input">Senior Frontend Engineer</div>
                    <div className="mock-ats-badge">🤖 ATS Score: 85%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 3: AI Magic */}
            <div className="step-row">
              <div className="step-content">
                <div className="step-number">03</div>
                <h2>AI Rewriting & Optimisation</h2>
                <p>
                  This is where the magic happens. We don't just inject
                  keywords; our AI rewrites your bullet points to focus on{" "}
                  <strong>measurable impact</strong>. We transform you from a
                  "task-doer" into an "outcome-driver."
                </p>
              </div>
              <div className="step-visual">
                <div className="abstract-ui-box ai-magic">
                  <div className="scanning-bar"></div>
                  <div className="ui-body">
                    <p className="typing-effect text-gradient">
                      Optimising impact metrics...
                    </p>
                    <div className="skeleton-line full"></div>
                    <div className="skeleton-line full delay-1"></div>
                    <div className="skeleton-line medium delay-2"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 4: Export (REVERSE LAYOUT) */}
            <div className="step-row reverse">
              <div className="step-content">
                <div className="step-number">04</div>
                <h2>Export & Apply with Confidence</h2>
                <p>
                  Choose from our library of ATS-tested, recruiter-approved
                  templates. Download your perfectly formatted PDF, knowing it's
                  mathematically optimised to beat the bots and impress human
                  eyes.
                </p>
              </div>
              <div className="step-visual">
                <div className="abstract-ui-box success-box">
                  <div className="check-circle">✓</div>
                  <h3>Ready for Submission</h3>
                  <p>Clean PDF Export Generated</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BEFORE & AFTER SECTION */}
        <section className="transformation-section">
          <h2>The Difference is in the Details</h2>
          <p className="section-lead">
            See how our AI transforms a standard bullet point into an
            interview-magnet.
          </p>

          <div className="before-after-grid">
            <div className="ba-card before">
              <div className="ba-badge">Before (Generic) ❌</div>
              <p>
                "Responsible for managing the database and improving query times
                for the company."
              </p>
              <ul className="ba-analysis">
                <li>No metrics or ROI</li>
                <li>Passive language ("Responsible for")</li>
                <li>Missed keyword opportunities</li>
              </ul>
            </div>

            <div className="ba-card after">
              <div className="ba-badge success">After (AI-Optimised) ✅</div>
              <p>
                "Engineered PostgreSQL database optimisations, reducing query
                latency by 45% and saving 20+ hours of manual data processing
                weekly."
              </p>
              <ul className="ba-analysis">
                <li>Clear impact (45%, 20+ hours)</li>
                <li>Strong action verbs ("Engineered")</li>
                <li>Targeted keywords ("PostgreSQL")</li>
              </ul>
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className="hiw-cta">
          <div className="hiw-cta-content">
            <h2>Stop Guessing. Start Interviewing.</h2>
            <div className="cta-wrapper" style={{ marginTop: "30px" }}>
              <button
                className="cta-button primary-pulse"
                onClick={() => navigate("/builder")}
              >
                Put Our AI to Work
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
